import { forwardRef, useId, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import type { InputHTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import { Icon } from "../Icon/Icon.js";
import styles from "./Field.module.scss";

/** How a field's label reads. `caption` is the quiet mono technical label (the default, and the shipped
 * majority); `heading` is the field's name as a small title, matching a section label; `quiet` is plain body text
 * in secondary ink, for a field that is an aside to the form (an optional note). */
export type FieldLabelVariant = "caption" | "heading" | "quiet";

/** How tightly a field is packed. See {@link FieldProps.density}. */
export type FieldDensity = "comfortable" | "compact";

// `children` is OMITTED deliberately. `Field` RENDERS the <input> — it is not a wrapper around one — so a
// child gets spread onto a void element and React throws at render ("input is a void element tag and must
// neither have `children`…"), taking the whole page down. That is what shipped on the release Details
// section, and nothing caught it: `InputHTMLAttributes` carries `children`, so `<Field><input/></Field>`
// typechecked, linted, and passed every test that never mounted that surface. Omitting it makes the mistake
// a COMPILE error. When the control itself must be supplied, that is `FieldShell` — the same
// label/hint/error frame with a render-prop child.
export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "children"> {
  /** Mono 9px uppercase label (Forms).*/
  label: string;
  /** Keep the label for screen readers only — when a group label already names the control. */
  hideLabel?: boolean;
  /** ADDITIVE error state: danger border + danger-ink caption wired via aria-describedby.*/
  error?: string;
  /** Optional helper/hint text under the field. */
  hint?: ReactNode;
  /** `caption` (default) — mono, tiny, tracked; the shipped majority. `heading` — Inter, a step larger,
   * full ink, matching a section label: for a surface that is ONE group of titled fields. */
  labelVariant?: FieldLabelVariant;
  /** Guidance shown BETWEEN the label and the control — what to type. Use `hint` for a note that belongs
   * UNDER the control instead. */
  subLabel?: ReactNode;
  /**
   * Hide this field from password managers: `autocomplete="off"` + `data-1p-ignore` + `data-lpignore`,
   * applied to the intrinsic input (where `data-*` is valid).
   *
   * **NOT for an account credential.** See {@link secureEntry} for the ruling and the scope.
   */
  antiAutofill?: boolean;
  /**
   * {@link antiAutofill} PLUS `type="text"` + CSS masking (`-webkit-text-security: disc`) — an opaque
   * secret rendered as dots that no manager will touch. Its remaining job is a secret that is **not this
   * account's credential**, of which there are two shapes in the app:
   *   - a **vendor secret** — a third party's password typed on our origin (the retired Bandsintown sign-in was
   *     the one instance), where every entry a manager could offer is the wrong account and a save would file a
   *     vendor password under this domain; and
   *   - a **minted gate** — the optional password on a release SHARE LINK, which is not a credential at all:
   *     it is set once on one link and never read back, so a manager has nothing to fill and nothing worth
   *     saving.
   *
   * Both are the case `autocomplete="off"` is the correct annotation for — and note it is not SUFFICIENT on
   * its own: browsers ignore it on a `type="password"` field and fill and offer to save anyway, which is why
   * dropping the password TYPE is the part that does the work.
   *
   * **NEVER an account password (PO ruling, 2026-09-06, ART-7).** It used to be exactly that, everywhere,
   * because iOS Password Manager once Face-ID-autofilled a saved credential OVER typed input and signed a
   * user into the WRONG ACCOUNT. The defense was real; the diagnosis was not. A field that is `type="text"`
   * with `autocomplete="off"` is not a password field to any browser, so:
   *   - **no "Save Password?" prompt can ever fire** — which is precisely what the SME reported ("passwords
   *     aren't saving"), against a doc comment right here that asserted Keychain save still worked. It
   *     never did, in either realm, on any password field.
   *   - it fails **WCAG 1.3.5 Identify Input Purpose (AA)**, and
   *   - on any engine without `-webkit-text-security` the password renders as **visible plaintext**.
   *
   * The wrong-account overwrite is what an UNANNOTATED login form looks like: with no
   * `autocomplete="username"` on the address, iOS cannot scope a saved credential to what was typed and
   * fills a guess into both fields. The fix is to annotate the form (`username` + `current-password` /
   * `new-password`), not to hide it — which is what every auth screen now does.
   */
  secureEntry?: boolean;
  /**
   * The eye button that shows what you are typing (ART-318). ON by default for every masked field — an account
   * password (`type="password"`) and a `secureEntry` box alike — so every password in the product can be checked
   * before it is submitted. Showing it only switches the RENDERING (`type="text"` / the mask off): the field keeps
   * its autocomplete tokens, so a password manager still knows what it is. `false` for a surface with its own
   * reveal control (the bus codes, one gate over several fields).
   */
  revealToggle?: boolean;
  /**
   * A short unit rendered INSIDE the control, before the value — `$` on a money field.
   *
   * It is inside the box, not floated in the label, because the two together read as one value: `$ 600` is
   * the figure, and a `$` parked in the label reads as a note about the field. The reference does the same
   * (`.in-wrap__pre`), which is why the money boxes line up as a row of `$`-led figures.
   *
   * Decorative by construction — `aria-hidden`, so a screen reader hears the LABEL ("Guarantee"), not
   * "dollar sign Guarantee". Keep it to a character or two; it is not a place for guidance (`subLabel` is).
   */
  affixStart?: ReactNode;
  /** The same, after the value — `%` on a rate. Also decorative. */
  affixEnd?: ReactNode;
  /**
   * `comfortable` (default) is the form-sized box: 16px of inline padding, an 8px label gap.
   *
   * `compact` is the WORKSHEET box — a grid of short numeric fields read as a row of figures rather than a
   * stack of form inputs (the settlement's door/splits/merch rows; the reference's `.in-wrap` at
   * `padding: 7px 10px` with a 4px label gap). It tightens padding and rhythm ONLY: height still comes from
   * `--ac-control-h`, so the touch target is unchanged and no call site can shrink a control on a phone.
   */
  density?: FieldDensity;
}

/** Types with nothing for the browser to usefully fill — a box you type prose into. An absent `type` is
 *  `text` by HTML default, so it counts. See the default in `Field` for why `email`/`tel`/`password` do not. */
function isFreeTextType(type: string | undefined): boolean {
  return type == null || type === "text" || type === "search" || type === "url";
}

/**
 * The base text Field: mono uppercase label, paper input with a 1.5px line-strong border and the
 * accent focus ring, and the ADDITIVE error state the MVP lacked (danger border + `aria-describedby` caption).
 * Ref-forwarding so react-hook-form's `register()` spread works.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, labelVariant, subLabel, hideLabel, error, hint, id, className, secureEntry, antiAutofill, affixStart, affixEnd, density, revealToggle = true, ...inputProps },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const subId = `${inputId}-sub`;
  const describedBy = [error ? errorId : null, hint ? hintId : null, subLabel ? subId : null].filter(Boolean).join(" ") || undefined;

  // ART-318: a masked field (a real password, or a CSS-masked secureEntry box) carries an eye that reveals it.
  const isPassword = (inputProps as { type?: string }).type === "password";
  const revealable = revealToggle && (isPassword || !!secureEntry);
  const [revealed, setRevealed] = useState(false);
  const masked = !!secureEntry && !revealed;
  // Applied AFTER the caller's props, so it overrides their `type="password"` while revealed.
  const revealProps = revealed && isPassword ? { type: "text" as const } : {};

  // Hide-from-manager attributes; secureEntry adds `type="text"` + CSS masking. Applied to the intrinsic
  // input (where data-* attributes are valid), and BEFORE `inputProps` — so a caller's own `type` /
  // `autoComplete` still wins, which is how ChangePasswordForm was already a real password field.
  // Account passwords do not come through here at all any more: see the `secureEntry` doc above.
  const wantsDefense = secureEntry || antiAutofill;
  const defenseProps = wantsDefense
    ? {
        autoComplete: "off" as const,
        autoCapitalize: "off" as const,
        autoCorrect: "off" as const,
        spellCheck: false,
        "data-1p-ignore": "",
        "data-lpignore": "true",
        ...(secureEntry ? { type: "text" as const } : {}),
      }
    // A FREE-TEXT box gets `autocomplete="off"` by default (PO, 2026-09-08). An unannotated input is one the
    // browser is free to guess at, and on a venue, a title or a note the guess is your own text from some
    // other form — a saved-form-entry dropdown that covers the field you are typing in and has nothing to
    // offer it. Reported as *"this massive test opportunity button that pops up while I'm typing"*.
    //
    // The carve-outs are the point, so they are a type test and not a list of call sites:
    //   · `password` NEVER — `autocomplete="off"` on a credential is precisely the state ART-7 removed
    //     (no browser treats such a field as a password, so no "Save password?" prompt can ever fire).
    //   · `email` / `tel` keep the browser's help, because there the guess is usually right and it is the
    //     one the user wants (their own address on a signup, their number on their own profile).
    //   · anything a caller declares wins outright — this spreads BEFORE `inputProps`, the same way the
    //     defense block above does. The 23 fields that want a real token already pass one.
    // It is deliberately NARROWER than `antiAutofill`: no `spellCheck: false`, no manager-hiding attributes.
    // Turning spellcheck off on a long note is a different decision from declining a form-history dropdown,
    // and this one is applied everywhere.
    : isFreeTextType((inputProps as { type?: string }).type)
      ? { autoComplete: "off" as const }
      : {};

  return (
    // `data-density` on the WRAPPER, so the label gap, the plain control and the affixed control's wrapper
    // are all reached by one attribute — a descendant override from a feature stylesheet could not touch the
    // affixed wrapper, which is where the padding actually lives for a `$`-led box.
    <div className={clsx(styles.field, error && styles.hasError, className)} data-density={density}>
      <label htmlFor={inputId} className={hideLabel ? styles.srOnly : styles.label} data-variant={labelVariant}>
        {label}
      </label>
      {subLabel && (
        <p id={subId} className={styles.subLabel}>
          {subLabel}
        </p>
      )}
      {revealable && affixStart == null && affixEnd == null ? (
        // A MASKED field (ART-318): the input stays the whole box — `.control` on the input itself — and the eye is
        // overlaid on its right end. Not the affixed wrapper, because the browser paints its AUTOFILL tint on the
        // input element: inside a wrapper only the text strip went yellow, with the eye's gutter left white.
        <span className={styles.revealWrap}>
          <input
            ref={ref}
            id={inputId}
            className={clsx(styles.control, styles.input, styles.revealInput, masked && styles.masked)}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...defenseProps}
            {...inputProps}
            {...revealProps}
          />
          <RevealButton revealed={revealed} onToggle={() => setRevealed((r) => !r)} disabled={inputProps.disabled} />
        </span>
      ) : affixStart != null || affixEnd != null ? (
        // AFFIXED: the WRAPPER wears `.control` (the fill, border, radius and — via :focus-within — the
        // focus ring), and the input inside is stripped bare. This is the `FieldShell` case the `.control`
        // comment above anticipates: a bespoke control shape inherits the height token without opting in,
        // so an affixed field is exactly as tall as a plain one beside it.
        <span className={clsx(styles.control, styles.affixed)}>
          {affixStart != null && (
            <span className={styles.affix} aria-hidden="true">
              {affixStart}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={clsx(styles.input, styles.bare, masked && styles.masked)}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            {...defenseProps}
            {...inputProps}
            {...revealProps}
          />
          {affixEnd != null && (
            <span className={styles.affix} aria-hidden="true">
              {affixEnd}
            </span>
          )}
          {revealable && (
            <RevealButton revealed={revealed} onToggle={() => setRevealed((r) => !r)} disabled={inputProps.disabled} />
          )}
        </span>
      ) : (
        <input
          ref={ref}
          id={inputId}
          // `.control` is the shared treatment (fill, border, radius, focus ring); `.input` only strips the
          // platform's own chrome. Without BOTH, the field renders as a bare native input — which is exactly
          // what shipped when the shared styles moved to `.control` and Textarea/Select/FieldShell were
          // updated but this one was not (fixed 2026-08-17).
          className={clsx(styles.control, styles.input, masked && styles.masked)}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...defenseProps}
          {...inputProps}
        />
      )}
      {hint && !error && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
});

/** The eye at the end of a masked field (ART-318). A real button, so it is reachable by keyboard and announces
 *  its state; `aria-pressed` carries whether the value is showing. It never submits the form it sits in. */
function RevealButton({ revealed, onToggle, disabled }: { revealed: boolean; onToggle: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      className={styles.reveal}
      aria-label={revealed ? "Hide password" : "Show password"}
      aria-pressed={revealed}
      disabled={disabled}
      // Keep focus in the input on a mouse press, so revealing mid-typing does not move the caret away.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onToggle}
    >
      <Icon icon={revealed ? EyeOff : Eye} size={16} />
    </button>
  );
}
